import type { WidgetDefinition } from '../WidgetDefinition';
import { faFilters } from '@fortawesome/pro-regular-svg-icons/faFilters';
import { generatorRoutine } from '@thinkalpha/ui-core/routine/index';
import type { ScannerWidgetModel } from '@thinkalpha/ui-types/contracts/workspace';
import { createDeferredIdeaHydration, shouldDeferIdeaHydration } from 'src/lib/widget/deferredIdeaHydration';
import { createDefaultScannerWidget } from 'src/store/defaults/widget';
import type { ScannerWidgetViewModel } from 'src/store/types';

class ScannerWidgetDefinition implements WidgetDefinition<ScannerWidgetViewModel, ScannerWidgetModel> {
    type = 'scanner' as const;
    nameKey = 'widgets::scanner::name' as const;
    roles = ['widgets::scanner'];
    icon = faFilters;
    category = 'widgets::categories::research' as const;
    defaultDimensions = { h: 0.7, w: 0.6, x: 0.2, y: 0.05 };

    /**
     * Converts a Scanner widget from contract model to view model.
     *
     * Handles three serialized forms of `idea`:
     *  - string id: clean saved idea, deferred to render-time hydration
     *  - { id, ... }: dirty draft of a saved idea, deferred to render-time hydration
     *  - { ... } without id: new unsaved idea
     */
    toViewModel = generatorRoutine(function* (_ctx, widget: ScannerWidgetModel) {
        const { idea: incomingIdea, ...restWidget } = widget;

        let idea: ScannerWidgetViewModel['idea'] | undefined;
        let isIdeaDirty = false;
        const ideaHydration = shouldDeferIdeaHydration(incomingIdea)
            ? createDeferredIdeaHydration(incomingIdea)
            : undefined;

        if (!ideaHydration && incomingIdea && typeof incomingIdea !== 'string') {
            idea = incomingIdea;
            isIdeaDirty = true;
        }

        return createDefaultScannerWidget({
            ...restWidget,
            idea,
            isIdeaDirty,
            ...(ideaHydration ? { ideaHydration } : {}),
        });
    });

    /**
     * Converts a Scanner widget from view model to contract model
     */
    toContractModel(widget: ScannerWidgetViewModel): ScannerWidgetModel {
        const {
            idea,
            ideaHydration,
            columnOptions,
            isIdeaDirty,
            defaultIdeaStatus: _defaultIdeaStatus,
            ...restWidget
        } = widget;

        return {
            ...restWidget,
            type: 'scanner',
            idea:
                ideaHydration && ideaHydration.status !== 'ready'
                    ? ideaHydration.source
                    : idea?.id && isIdeaDirty === false
                      ? idea.id
                      : idea,
            columnOptions,
        };
    }
}

export const scannerWidgetDefinition = new ScannerWidgetDefinition();
